import React, { useCallback, useContext, useEffect } from "react";
import i18next from "i18next";
import _ from "lodash";
import { useFormContext, useWatch } from "react-hook-form";

import useStyles from "style/js-style/containers/administration/calculator/CalculatorDialogStyle";
import EAIcon from "components/common/EAIcon";
import EAButton from "components/common/EAButton";
import Branches from "containers/administration/calculator/form/branches";
import {
  CalculationDeclarationContext,
  getDefaultBranchModel,
  getBranchModel,
  getVariableModel,
  isValidBranch,
  parseTargetFieldFromVariable,
  VariableType,
  CalculationResultType,
  ExecutionMode,
} from "services/common/CalculatorUtils";
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext
} from "services/common/RolesUtils";
import clsx from "clsx";
import Variables from 'containers/administration/calculator/form/variables';

const DeclarationStep = ({
  isEditionMode = false,
  isActiveCalculation = false,
  onCancel = () => { },
  onContinue = () => { },
  onDelete = () => { },
  readOnly = false,
  onClose = () => { },
  isSaveLoading = false,
  isActivateLoading = false,
  executionMode = ExecutionMode.FIRST_MATCHING_BRANCH_RESULT
}) => {
  const { classes } = useStyles();
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const formMethods = useFormContext();
  const {
    formState,
    formState: { errors },
    setValue,
    handleSubmit,
    getValues,
    trigger,
    clearErrors
  } = formMethods;

  const [onVariableChangeCallBacks, setOnVariableChangeCallBacks] = React.useState({});
  const [duplicateVariablesIds, setDuplicateVariablesIds] = React.useState([]);
  const [isDisabledAction, setIsDisabledAction] = React.useState(true);
  const [saveInProgress, setSaveInProgresss] = React.useState(false);

  let variables = useWatch({ name: `variables` });
  let branches = useWatch({ name: `branches` });
  let resultType = useWatch({ name: `resultType` });

  // Vérifie que la variable est correctement définie et qu'elle n'est pas un doublon
  const isValidVariable = useCallback((variable) => {
    if (!variable) return false;

    const { name, id } = variable;
    const field = parseTargetFieldFromVariable(variable);
    return name !== "" && !duplicateVariablesIds.includes(id) && field !== undefined && field !== "";
  }, [duplicateVariablesIds]);

  // Ajoute l'id de la variable si c'est un doublon
  const addDuplicateVariable = useCallback((variableId) => {
    if (!duplicateVariablesIds.includes(variableId)) {
      setDuplicateVariablesIds(prev => [...prev, variableId]);
    }
  }, [duplicateVariablesIds]);

  // Supprime l'id de la variable si elle est contenu dans duplicateVariablesIds
  const removeDuplicateVariable = useCallback((variableId) => {
    if (duplicateVariablesIds.includes(variableId)) {
      setDuplicateVariablesIds(prev => [...prev.filter(entry => entry !== variableId)]);
    }
  }, [duplicateVariablesIds]);

  // Associe une callBack a un id de critère
  const addCallback = (id, callBack) => {
    setOnVariableChangeCallBacks((prevCallbacks) => {
      const updatedCallbacks = { ...prevCallbacks };
      updatedCallbacks[id] = callBack;
      return updatedCallbacks;
    });
  };

  // Suppression de la callBack
  const removeCallBack = (id) => {
    setOnVariableChangeCallBacks((prevCallbacks) => {
      const updatedCallbacks = { ...prevCallbacks };
      if (updatedCallbacks[id]) {
        delete updatedCallbacks[id];
      }
      return updatedCallbacks;
    });
  };

  const submitForm = useCallback((data) => {
    onContinue(data);
  }, [onContinue]);

  const checkVariables = useCallback(() => {
    return !_.isEmpty(variables) || _.every(variables, isValidVariable);
  }, [variables, isValidVariable]);

  const checkBranches = useCallback(() => {
    let bValid = false;
    const defaultBranch = _.find(branches, { default: true });

    if (defaultBranch && isValidBranch(defaultBranch)) {
      const otherBranches = _.filter(branches, { default: false });

      bValid =  (!_.isEmpty(otherBranches) || CalculationResultType.ARITHMETIC_FORMULA_OUTPUT === resultType) && _.every(otherBranches, isValidBranch)
    }

    return bValid;
  }, [branches, resultType]);

  useEffect(() => {
    if(saveInProgress){
      setIsDisabledAction(true);
    }else{
      setIsDisabledAction(!_.isEmpty(errors) || !checkVariables() || !checkBranches());
    }
  }, [errors, checkVariables, checkBranches, formState, saveInProgress]);

  const handleSave = useCallback(async () => {
    // Désactive l'enregistrement le temps que le trigger s'exécute
    setSaveInProgresss(true);
    await trigger();
    setSaveInProgresss(false);

    if (_.isEmpty(formState.errors)) {
      if (isEditionMode) {
        onContinue();
      } else {
        await handleSubmit(submitForm)();
      }
    }
  }, [handleSubmit, submitForm, isEditionMode, formState, onContinue, trigger]);

  const context = React.useMemo(
    () => ({
      addCallback: addCallback,
      removeCallBack: removeCallBack,
      isValidVariable: isValidVariable,
    }),
    [
      isValidVariable,
    ]
  );

  return (
    <CalculationDeclarationContext.Provider value={context}>
      <div className={classes.rootDeclaration}>
        <EAIcon icon="cross" onClick={() => onClose()} className={classes.cross} />
        <div className={classes.declarationHeader}>
          <span className={classes.declarationHeaderCalcul}>
            {i18next.t("calculator.dialog.create.calculation")} :
          </span>
          <span className={classes.declarationHeaderTitle}>
            &nbsp;{getValues("name")}
          </span>
          <span className={classes.declarationHeaderType}>
            {i18next.t("calculator.dialog.create.natureOption." + getValues("nature"))}
          </span>
        </div>

        {/* --- Variables --- */}
        <Variables
          fieldName={`variables`}
          onVariableChangeCallBacks={onVariableChangeCallBacks}
          removeDuplicateVariable={removeDuplicateVariable}
          addDuplicateVariable={addDuplicateVariable}
          resultType={resultType}
          readOnly={readOnly}
        />

        {/* --- Branches --- */}
        <Branches fieldName={`branches`} resultType={resultType} executionMode={executionMode} readOnly={readOnly}/>

        {/* --- Actions --- */}
        <div className={classes.declarationControls}>
          {/* Supprimer */}
          {isEditionMode && !readOnly ?
            <div
              className={clsx(classes.declarationControlsLeft, !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_CALCULATION) ? classes.disabledDeleteBtn : classes.deleteCalc)}
              onClick={() => {
                if (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_CALCULATION)) {
                  onDelete();
                }
              }}
            >
              <EAIcon icon="delete_red" style={{ width: "10px" }} />
              <span>{i18next.t("calculator.dialog.create.deleteCalculation")}</span>
            </div> : <div />
          }

          {/* Annuler / Enregistrer / Activer */}
          <div className={classes.declarationControlsRight}>
            <EAButton
              onClick={(e) => {
                e.preventDefault();

                if (isEditionMode) {
                  formMethods.reset();
                } else {
                  setValue("variables", [getVariableModel(VariableType.CRITERION_ARGUMENT)]);
                  setValue("branches", [getDefaultBranchModel(), getBranchModel()]);
                  clearErrors();
                }

                onCancel();
              }}
              content={i18next.t("cancel")}
              customStyle={{ width: "80px" }}
              colorVariant={"secondary"}
            />

            <EAButton
              onClick={(e) => {
                e.preventDefault();
                handleSave();
              }}
              content={i18next.t("save")}
              colorVariant={"primaryLight"}
              customStyle={{ width: "117px" }}
              disabled={isDisabledAction || readOnly}
              validateLoading={isSaveLoading || saveInProgress}
            />

            {(!isEditionMode || !isActiveCalculation) ? (
              <EAButton
                onClick={(e) => {
                  e.preventDefault();
                  if (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS)) {
                    setValue(`enabled`, true);
                    handleSave();
                  }
                }}
                content={i18next.t("activate")}
                customStyle={{ width: "117px" }}
                disabled={isDisabledAction || !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS) || readOnly}
                validateLoading={isActivateLoading}
              />
            ) : (
              <EAButton
                onClick={(e) => {
                  e.preventDefault();
                  if (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS)) {
                    setValue(`enabled`, false);
                    handleSave();
                  }
                }}
                content={i18next.t("deactivate")}
                colorVariant={"success"}
                customStyle={{ width: "117px" }}
                disabled={isDisabledAction || !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_CALCULATION_STATUS) || readOnly}
                validateLoading={isActivateLoading}
              />
            )}
          </div>
        </div>
      </div>
    </CalculationDeclarationContext.Provider>
  );
}

export default DeclarationStep;
